import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { formErrorMsg } from "core/config/constants";
import { useAuthStore } from "core/store/authStore";
import { getDashboardRoute, getProviderLandingRoute } from "core/router/routes";
import "../../login/components/login.css";

interface SignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

const SignUp = () => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register: registerUser, isLoading, error, clearError } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpFormData>();

  const onSubmit = async (formData: SignUpFormData) => {
    clearError();
    const intent = searchParams.get("intent");
    const role =
      intent === "provider" || intent === "runner" ? "provider" : "customer";
    try {
      const registeredUser = await registerUser({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: '',
        role,
        agreedToTerms: true,
      });
      const destination =
        registeredUser.role === 'provider'
          ? await getProviderLandingRoute()
          : getDashboardRoute(registeredUser.role);
      navigate(destination);
    } catch {
      // error is already set in the store
    }
  };

  return (
    <div className="login-page">
      <header className="login-brand">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="bg-primary p-1.5 rounded-xl shadow-lg shadow-primary/25">
            <span
              className="material-symbols-outlined text-white text-[22px]"
              style={{ fontVariationSettings: '"FILL" 1' }}
            >
              directions_run
            </span>
          </div>
          <h2 className="text-[1.4rem] font-black tracking-tighter">Errandi</h2>
        </Link>
      </header>
      <div className="login-card">
        {(searchParams.get("intent") === "provider" || searchParams.get("intent") === "runner") ? (
          <div style={{ marginBottom: "1.25rem" }}>
            <h1 className="text-xl font-black tracking-tight text-slate-900">Register as a provider</h1>
            <p className="text-sm text-slate-500 mt-1">Create your account, then set up your organisation and vehicle.</p>
          </div>
        ) : null}

        {error && (
          <div className="login-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="login-form">
          <div className="form-group">
            <label htmlFor="firstName" className="form-label">
              First Name
            </label>
            <input
              id="firstName"
              type="text"
              autoComplete="given-name"
              className={`form-input ${errors.firstName ? "form-input-error" : ""}`}
              placeholder="Jane"
              {...register("firstName", {
                required: formErrorMsg.required,
              })}
            />
            {errors.firstName && (
              <span className="form-error-text">
                {errors.firstName.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="lastName" className="form-label">
              Last Name
            </label>
            <input
              id="lastName"
              type="text"
              autoComplete="family-name"
              className={`form-input ${errors.lastName ? "form-input-error" : ""}`}
              placeholder="Doe"
              {...register("lastName", {
                required: formErrorMsg.required,
              })}
            />
            {errors.lastName && (
              <span className="form-error-text">
                {errors.lastName.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className={`form-input ${errors.email ? "form-input-error" : ""}`}
              placeholder="you@example.com"
              {...register("email", {
                required: formErrorMsg.required,
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: formErrorMsg.invalidEmail,
                },
              })}
            />
            {errors.email && (
              <span className="form-error-text">
                {errors.email.message}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <div className="password-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                className={`form-input ${errors.password ? "form-input-error" : ""}`}
                placeholder="Create a password"
                {...register("password", {
                  required: formErrorMsg.required,
                  validate: (value) => {
                    if (value.length < 8) return formErrorMsg.minPass;
                    if (!/[A-Z]/.test(value)) return 'Password must contain at least one uppercase letter';
                    if (!/[a-z]/.test(value)) return 'Password must contain at least one lowercase letter';
                    if (!/[0-9]/.test(value)) return 'Password must contain at least one number';
                    if (!/[^A-Za-z0-9]/.test(value)) return 'Password must contain at least one special character';
                    return true;
                  },
                })}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label="Toggle password visibility"
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            {errors.password ? (
              <span className="form-error-text">
                {errors.password.message}
              </span>
            ) : (
              <span className="form-hint-text">
                8+ characters with uppercase, lowercase, number, and special character
              </span>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="login-button"
          >
            {isLoading ? "Creating account…" : "Sign up"}
          </button>
        </form>

        <p className="login-footer">
          Already have an account?{" "}
          <Link to="/login" className="login-link">
            Log in
          </Link>
        </p>

        <p className="login-footer" style={{ marginTop: "0.5rem", fontSize: "0.8rem" }}>
          By creating an account, you agree to Errandi&apos;s{" "}
          <Link to="/terms-of-service" className="login-link">
            Terms of Service
          </Link>
        </p>
      </div>

      <footer className="w-full px-6 lg:px-20 pb-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between pt-10 border-t border-primary/6 gap-6">
          <div className="flex gap-5">
            <button type="button" className="text-slate-400 hover:text-primary transition-colors bg-transparent border-0 p-0 cursor-pointer">
              <span className="material-symbols-outlined">social_leaderboard</span>
            </button>
            <button type="button" className="text-slate-400 hover:text-primary transition-colors bg-transparent border-0 p-0 cursor-pointer">
              <span className="material-symbols-outlined">share</span>
            </button>
            <button type="button" className="text-slate-400 hover:text-primary transition-colors bg-transparent border-0 p-0 cursor-pointer">
              <span className="material-symbols-outlined">alternate_email</span>
            </button>
          </div>
          <p className="text-slate-400 text-sm font-medium">&copy; 2026 Errandi Technologies Ltd. All rights reserved.</p>
          <div className="flex gap-3 text-xs font-semibold text-slate-400">
            <a href="/privacy-policy" className="hover:text-primary transition-colors">Privacy</a>
            <span>&middot;</span>
            <a href="/terms-of-service" className="hover:text-primary transition-colors">Terms</a>
            <span>&middot;</span>
            <a href="/cookies-policy" className="hover:text-primary transition-colors">Cookies</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SignUp;
